import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { formatDate, formatCurrency, formatDateLocal, calculateAge } from '../../utils/helper';
import { toast } from 'react-toastify';
import { getAppointmentStatus } from '../../utils/statusHelper';
import AuthService from '../../services/AuthService';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const List = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoader, setIsLoader] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [pagination, setPagination] = useState({});
    const authUser = AuthService.getCurrentUser();
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const navigate = useNavigate();
    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
        start_date: "",
        end_date: "",
        recommend_specialist: "",
        status: "",
        type: searchParams.get('type') || "doctor"
    });

    useEffect(() => {
        getAppointments();
        const interval = setInterval(() => {
            getAppointments(true);
        }, 10000);
        return () => clearInterval(interval);
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.recommend_specialist, apiData.status, apiData.type]); 

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    }; 

    const getAppointments = async (isBackground = false) => {
        try {
            if (!isBackground) setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/appointments", 
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                const BOTTOM_STATUSES = ['checkup_completed', 'cancelled'];
                const sorted = (data.data.appointments || []).slice().sort((a, b) => {
                    const aBottom = BOTTOM_STATUSES.includes(a.status) ? 1 : 0;
                    const bBottom = BOTTOM_STATUSES.includes(b.status) ? 1 : 0;
                    return aBottom - bBottom;
                });
                setAppointments(sorted);
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (!isBackground) setIsLoader(false);
        }
    };

    const deleteAppointment = async (id) => {
        if (!window.confirm("Are you sure you want to delete this appointment?")) return;
        
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/appointments/${id}`,
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getAppointments();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setIsLoader(false);
        }
    };

    const moveAppointment = async (id, direction) => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "POST",
                url: `admin/appointments/${id}/move`,
                data: { direction }
            });
            if (response.data.status) {
                // toast.success(response.data.message);
                getAppointments();
            } else {
                // toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Appointments
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        {apiData.type !== 'specialist' && ['admin','receptionist'].includes(authUser.role) && (
                            <Link to="/appointments/create" className="btn btn-sm fw-bold btn-base">
                                Create Appointment
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                         <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Appointment"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <select 
                                            className="form-select form-select-solid"
                                            value={apiData.status}
                                            onChange={(e) => setApiData({...apiData, status: e.target.value})}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="queue">Queue</option>
                                            <option value="pending">Pending (Pre-test Done)</option>
                                            <option value="checkup_completed">Checkup Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <select 
                                            className="form-select form-select-solid"
                                            value={apiData.recommend_specialist}
                                            onChange={(e) => setApiData({...apiData, recommend_specialist: e.target.value})}
                                        >
                                            <option value="">All Recommendations</option>
                                            <option value="Yes">Specialist Recommended</option>
                                            <option value="No">No Recommendation</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                                {['doctor', 'admin','receptionist'].includes(authUser?.role) && (
                                    <li className="nav-item">
                                        <a 
                                            className={`nav-link cursor-pointer ${apiData.type === 'doctor' ? 'active' : ''}`}
                                            onClick={() => {
                                                setSearchParams({ type: 'doctor' });
                                                setApiData({...apiData, type: 'doctor', page: 1});
                                            }}
                                        >
                                            Doctor Appointments
                                        </a>
                                    </li>
                                )}
                                {['specialist', 'admin','receptionist'].includes(authUser?.role) && (
                                <li className="nav-item">
                                    <a 
                                        className={`nav-link cursor-pointer ${apiData.type === 'specialist' ? 'active' : ''}`}
                                        onClick={() => {
                                            setSearchParams({ type: 'specialist' });
                                            setApiData({...apiData, type: 'specialist', page: 1});
                                        }}
                                    >
                                        Specialist Appointments
                                    </a>
                                </li>
                                )}
                            </ul>
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-100px">Ref No</th>
                                                <th className="min-w-50px">Token</th>
                                                <th className="min-w-125px">Patient</th>
                                                <th className="min-w-100px">Checkup Type</th>
                                                <th className="min-w-125px">{apiData.type === 'doctor' ? 'Doctor' : 'Specialist'}</th>
                                                <th className="min-w-125px">Date & Time</th>
                                                <th className="min-w-125px">Status</th>
                                        <th className="min-w-125px">Payment</th>
                                        <th className="min-w-125px">Amount</th>
                                        {apiData.type === 'doctor' && <th className="min-w-125px">Rec. Specialist</th>}
                                        <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {appointments.map((appt, index) => (
                                                <tr key={index}>
                                                    <td className='cursor-pointer' onClick={() => navigate(`/appointments/${appt.id}/checkup`)}>{appt.ref_no}</td>
                                                    <td><span className="badge badge-light-primary fs-7 fw-bold">{appt.token_no}</span></td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-800 fw-bold mb-1">{appt.patient?.name}</span>
                                                            <span className="text-muted fs-7">
                                                                {appt.patient?.date_of_birth ? `Age: ${calculateAge(appt.patient.date_of_birth)}` : ''}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-light-${appt.checkup_type?.title === 'IOP' ? 'info' : 'primary'} fs-7 fw-bold`}>
                                                            {appt.checkup_type?.title || '-'}
                                                        </span>
                                                    </td>
                                                    <td>{apiData.type === 'doctor' ? appt.doctor?.name : appt.specialist?.name}</td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-800 fw-bold">{formatDate(appt.date, 'D MMM, YYYY')}</span>
                                                            <span className="text-gray-500 fw-semibold">{appt.time}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getAppointmentStatus(appt.status).badgeClass}`}>
                                                            {getAppointmentStatus(appt.status).label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge fw-bold fs-7 ${appt.payment_status === 'paid' ? 'badge-light-success' : appt.payment_status === 'partially_paid' ? 'badge-light-warning' : 'badge-light-danger'}`}>
                                                            {appt.payment_status ? appt.payment_status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                        </span>
                                                    </td>
                                                    <td>{formatCurrency(appt.price)}</td>
                                                    {apiData.type === 'doctor' && (
                                                    <td>
                                                        {(appt.doctor_records?.specialist_recommendation === 'Yes' || appt.receptionist_records?.specialist_recommendation === 'Yes') ? (
                                                            <span className="badge badge-light-danger fw-bold">Yes</span>
                                                        ) : (
                                                            <span className="badge badge-light-success fw-bold">No</span>
                                                        )}
                                                    </td>
                                                    )}
                                                    <td className="text-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Move Up</Tooltip>}>
                                                            <button onClick={() => moveAppointment(appt.id, 'up')} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-arrow-up"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Move Down</Tooltip>}>
                                                            <button onClick={() => moveAppointment(appt.id, 'down')} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-arrow-down"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Perform Checkup</Tooltip>}>
                                                            <Link to={apiData.type === 'specialist' ? `/specialist-checkup/${appt.id}` : `/appointments/${appt.id}/checkup`} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-stethoscope"></i>
                                                            </Link>
                                                        </OverlayTrigger>
                                                        {['admin', 'receptionist'].includes(authUser.role) && (
                                                            <>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Create Sales Order</Tooltip>}>
                                                                <Link to={`/orders/create?patient_id=${appt.patient?.id}`} className="btn btn-icon btn-light btn-active-light-success btn-sm me-2">
                                                                    <i className="fas fa-shopping-cart"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Delete Appointment</Tooltip>}>
                                                                <button onClick={() => deleteAppointment(appt.id)} className="btn btn-icon btn-light btn-active-light-danger btn-sm">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {appointments.length === 0 && (
                                                <tr>
                                                    <td colSpan="11" className="text-center">No appointments found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
